import { useState } from 'react';
import type { Question } from '@/types';
import { isCorrect } from '@/lib/quiz';

const LETTERS = 'ABCDEFGH';

interface Props {
  question: Question;
  alreadyAnswered: number[] | null;
  onAnswer: (answer: number[], correct: boolean) => void;
  onNext: () => void;
  isLast: boolean;
  /**
   * Exam mode: every question is a two-step commit, so a choice can be changed
   * until it is checked. The source line stays hidden until then so it can't
   * hint at the answer.
   */
  examMode?: boolean;
}

export default function QuestionCard({
  question,
  alreadyAnswered,
  onAnswer,
  onNext,
  isLast,
  examMode = false,
}: Props) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [submittedAnswer, setSubmittedAnswer] = useState<number[] | null>(alreadyAnswered);

  const isSelectAll = question.type === 'select-all';
  const answered = submittedAnswer !== null;
  const correct = answered && isCorrect(question, submittedAnswer);
  /** Select-all has always been pick-then-submit; exam mode extends that to every type. */
  const twoStep = isSelectAll || examMode;

  const badgeText = isSelectAll ? 'Select all that apply' : question.type === 'scenario' ? 'Scenario' : 'Recall';

  const handleSelect = (i: number) => {
    if (answered) return;
    if (!twoStep) {
      const answer = [i];
      setSubmittedAnswer(answer);
      onAnswer(answer, isCorrect(question, answer));
      return;
    }
    setSelected((prev) => {
      if (!isSelectAll) return new Set([i]);
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const submit = () => {
    if (answered || selected.size === 0) return;
    const answer = [...selected].sort((a, b) => a - b);
    setSubmittedAnswer(answer);
    onAnswer(answer, isCorrect(question, answer));
  };

  const optionClass = (i: number): string => {
    const classes = ['option'];
    if (answered) {
      classes.push('disabled');
      if (question.ans.includes(i)) {
        if (isSelectAll && submittedAnswer && !submittedAnswer.includes(i)) classes.push('missed');
        else classes.push('correct');
      } else if (submittedAnswer && submittedAnswer.includes(i)) {
        classes.push('wrong');
      }
    } else if (selected.has(i)) {
      classes.push('selected');
    }
    return classes.join(' ');
  };

  const hint = isSelectAll
    ? '⬡ Tap all correct answers, then check'
    : '⬡ Tap your answer — you can change it before checking';

  return (
    <div className="question-card">
      {(!examMode || answered) && <div className="q-source">{question.source}</div>}
      <span className={`q-type-badge ${isSelectAll ? 'select-all' : question.type}`}>{badgeText}</span>
      <div className="q-text">{question.q}</div>
      {question.scenario && <div className="q-scenario">{question.scenario}</div>}
      {twoStep && !answered && <div className="select-all-note">{hint}</div>}

      <div className="options">
        {question.opts.map((text, i) => (
          <div key={i} className={optionClass(i)} onClick={() => handleSelect(i)}>
            <div className="option-letter">{LETTERS[i]}</div>
            <div className="option-text">{text}</div>
          </div>
        ))}
      </div>

      {twoStep && !answered && (
        <div className="sa-submit">
          <button className="btn btn-primary btn-sm" onClick={submit} disabled={selected.size === 0}>
            Check Answer
          </button>
        </div>
      )}

      {answered && (
        <div className={`feedback ${correct ? 'correct-fb' : 'wrong-fb'}`}>
          <div className="fb-title">{correct ? '✓ Correct!' : '✗ Incorrect'}</div>
          <div>{question.fb}</div>
          {question.ansNote && (
            <div className="fb-ansnote"><strong>Full answer:</strong> {question.ansNote}</div>
          )}
          {!correct && question.example && (
            <div className="fb-example">
              <strong>Example to help you remember:</strong><br />
              {question.example}
            </div>
          )}
        </div>
      )}

      {answered && (
        <div className="next-area">
          <button className="btn btn-primary" onClick={onNext}>
            {isLast ? 'See Results →' : 'Next →'}
          </button>
        </div>
      )}
    </div>
  );
}
